import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const uid = () => Math.random().toString(36).slice(2, 10);

export const TimetableModuleEditor = ({ data, onChange }) => {
  const prices = data.prices || [];
  const groups = data.groups || [];
  const [openGroup, setOpenGroup] = useState(0);

  const setField = (field, value) => onChange({ ...data, [field]: value });

  const setPrice = (i, field, value) => {
    const next = prices.map((p, idx) => (idx === i ? { ...p, [field]: value } : p));
    setField('prices', next);
  };

  const setGroup = (i, patch) => {
    const next = groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g));
    setField('groups', next);
  };

  const setDay = (gi, di, patch) => {
    const schedule = (groups[gi].schedule || []).map((d, idx) => (idx === di ? { ...d, ...patch } : d));
    setGroup(gi, { schedule });
  };

  const setEntry = (gi, di, ei, field, value) => {
    const entries = (groups[gi].schedule[di].entries || []).map((e, idx) => (idx === ei ? { ...e, [field]: value } : e));
    setDay(gi, di, { entries });
  };

  return (
    <div className="space-y-8" data-testid="timetable-editor">
      <div>
        <Label className="text-slate-300">Section Title</Label>
        <Input
          value={data.title || ''}
          onChange={(e) => setField('title', e.target.value)}
          placeholder="Classes & Timetable"
          className="mt-1.5 bg-slate-800 border-slate-700 text-white"
          data-testid="timetable-title-input"
        />
      </div>

      {/* Pricing */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-white font-semibold">Pricing</h4>
          <Button variant="outline" size="sm" onClick={() => setField('prices', [...prices, { label: '', price: '' }])} data-testid="add-price-btn" className="border-slate-600 text-slate-300">
            <Plus className="w-4 h-4 mr-1" /> Add Price
          </Button>
        </div>
        {prices.map((p, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={p.price} onChange={(e) => setPrice(i, 'price', e.target.value)} placeholder="£7" className="w-28 bg-slate-800 border-slate-700 text-white" data-testid={`price-amount-${i}`} />
            <Input value={p.label} onChange={(e) => setPrice(i, 'label', e.target.value)} placeholder="30 min group class" className="flex-1 bg-slate-800 border-slate-700 text-white" data-testid={`price-label-${i}`} />
            <button onClick={() => setField('prices', prices.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 p-2" data-testid={`remove-price-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-400 text-xs">Highlight badge (optional)</Label>
            <Input value={data.badge || ''} onChange={(e) => setField('badge', e.target.value)} placeholder="First Trial Class Free" className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="timetable-badge-input" />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Small print note (optional)</Label>
            <Input value={data.note || ''} onChange={(e) => setField('note', e.target.value)} placeholder="Discounts, terms..." className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="timetable-note-input" />
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-white font-semibold">Schedule Columns <span className="text-slate-500 text-sm font-normal">(e.g. Dance / Singing / Acting)</span></h4>
          <Button
            variant="outline" size="sm"
            onClick={() => { setField('groups', [...groups, { id: uid(), title: '', price: '', description: '', schedule: [] }]); setOpenGroup(groups.length); }}
            data-testid="add-group-btn" className="border-slate-600 text-slate-300"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Column
          </Button>
        </div>

        {groups.map((group, gi) => (
          <div key={group.id || gi} className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-testid={`timetable-group-${gi}`}>
            <button className="w-full flex items-center justify-between p-4" onClick={() => setOpenGroup(openGroup === gi ? -1 : gi)} data-testid={`toggle-group-${gi}`}>
              <span className="text-white font-medium">{group.title || `Column ${gi + 1}`}</span>
              {openGroup === gi ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {openGroup === gi && (
              <div className="p-4 pt-0 space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                  <Input value={group.title} onChange={(e) => setGroup(gi, { title: e.target.value })} placeholder="Dance" className="bg-slate-800 border-slate-700 text-white" data-testid={`group-title-${gi}`} />
                  <Input value={group.price || ''} onChange={(e) => setGroup(gi, { price: e.target.value })} placeholder="From £7 (chip, optional)" className="bg-slate-800 border-slate-700 text-white" data-testid={`group-price-${gi}`} />
                  <Button variant="ghost" size="sm" onClick={() => setField('groups', groups.filter((_, idx) => idx !== gi))} className="text-red-400 hover:text-red-300 justify-self-end" data-testid={`remove-group-${gi}`}>
                    <Trash2 className="w-4 h-4 mr-1" /> Remove Column
                  </Button>
                </div>
                <Textarea value={group.description || ''} onChange={(e) => setGroup(gi, { description: e.target.value })} placeholder="Short intro for this column (optional)" rows={2} className="bg-slate-800 border-slate-700 text-white resize-none" data-testid={`group-desc-${gi}`} />

                {(group.schedule || []).map((dayGroup, di) => (
                  <div key={di} className="border border-slate-700 rounded-lg p-3 space-y-2">
                    <div className="flex gap-2 items-center">
                      <Input value={dayGroup.day} onChange={(e) => setDay(gi, di, { day: e.target.value })} placeholder="Monday" className="w-44 bg-slate-800 border-slate-700 text-white font-medium" data-testid={`day-name-${gi}-${di}`} />
                      <Button variant="ghost" size="sm" onClick={() => setDay(gi, di, { entries: [...(dayGroup.entries || []), { name: '', time: '' }] })} className="text-slate-300" data-testid={`add-entry-${gi}-${di}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Class
                      </Button>
                      <button onClick={() => setGroup(gi, { schedule: group.schedule.filter((_, idx) => idx !== di) })} className="text-red-400 hover:text-red-300 p-1.5 ml-auto" data-testid={`remove-day-${gi}-${di}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {(dayGroup.entries || []).map((entry, ei) => (
                      <div key={ei} className="flex gap-2 items-center pl-2">
                        <Input value={entry.name} onChange={(e) => setEntry(gi, di, ei, 'name', e.target.value)} placeholder="Street Dance · Juniors (6+)" className="flex-1 bg-slate-800 border-slate-700 text-white text-sm h-8" data-testid={`entry-name-${gi}-${di}-${ei}`} />
                        <Input value={entry.time} onChange={(e) => setEntry(gi, di, ei, 'time', e.target.value)} placeholder="4:45–5:45pm" className="w-36 bg-slate-800 border-slate-700 text-white text-sm h-8" data-testid={`entry-time-${gi}-${di}-${ei}`} />
                        <button onClick={() => setDay(gi, di, { entries: dayGroup.entries.filter((_, idx) => idx !== ei) })} className="text-red-400/70 hover:text-red-300 p-1" data-testid={`remove-entry-${gi}-${di}-${ei}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setGroup(gi, { schedule: [...(group.schedule || []), { day: '', entries: [{ name: '', time: '' }] }] })} className="border-slate-600 text-slate-300" data-testid={`add-day-${gi}`}>
                  <Plus className="w-4 h-4 mr-1" /> Add Day
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
